import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link as MuiLink, Paper, Stack, TextField,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ApiService from '../network/API';
import {confirmDialog} from '../confirm';

const errText = (e, fallback) => (typeof e === 'string' ? e : e?.reason || e?.message) || fallback;

const ACTION = {
    WRITE: {label: 'впишем', color: 'success'},
    DONE: {label: 'уже стоит', color: 'default'},
    CONFLICT: {label: 'в документе другая тема — не трогаем', color: 'warning'},
};

// Перенос утверждённых тем в служебную записку (.docx на Google Диске): ссылка, предпросмотр, запись по кнопке, копии файла.
const CourseworkDocPanel = ({courseworkId, docUrl, onDocUrlChange, onToast}) => {
    const [url, setUrl] = useState(docUrl || '');
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [writing, setWriting] = useState(false);
    const [error, setError] = useState(null);
    const [backups, setBackups] = useState([]);

    useEffect(() => setUrl(docUrl || ''), [docUrl]);

    const loadBackups = useCallback(() => {
        ApiService.getCourseworkDocBackups(courseworkId).then(setBackups).catch(() => {});
    }, [courseworkId]);

    useEffect(() => { loadBackups(); }, [loadBackups]);

    const save = async () => {
        setSaving(true);
        try {
            await ApiService.setCourseworkDoc(courseworkId, url);
            onDocUrlChange(url.trim() || null);
            onToast({severity: 'success', message: 'Ссылка на документ сохранена'});
        } catch (e) {
            onToast({severity: 'error', message: errText(e, 'Не удалось сохранить ссылку')});
        } finally {
            setSaving(false);
        }
    };

    const openPreview = async () => {
        setError(null);
        setPreview(null);
        setLoadingPreview(true);
        try {
            setPreview(await ApiService.previewCourseworkDoc(courseworkId));
        } catch (e) {
            setError(errText(e, 'Не удалось прочитать документ'));
        } finally {
            setLoadingPreview(false);
        }
    };

    const write = async () => {
        if (!await confirmDialog(`Вписать темы в документ (${preview.toWrite})? Перед записью будет сохранена копия файла.`)) return;
        setWriting(true);
        try {
            const r = await ApiService.transferCourseworkDoc(courseworkId);
            onToast({severity: r.written ? 'success' : 'info', message: r.message});
            setPreview(null);
            setError(null);
            loadBackups();
        } catch (e) {
            setError(errText(e, 'Не удалось записать в документ'));
        } finally {
            setWriting(false);
        }
    };

    const download = async (b) => {
        try {
            const blob = await ApiService.downloadCourseworkDocBackup(b.id);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `${(b.fileName || 'document').replace(/\.docx$/i, '')} — копия ${b.id}.docx`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            onToast({severity: 'error', message: errText(e, 'Не удалось скачать копию')});
        }
    };

    const restore = async (b) => {
        if (!await confirmDialog(`Вернуть документ к копии #${b.id}? Все изменения, сделанные в нём после неё (в том числе другими преподавателями), пропадут. Текущая версия перед этим тоже сохранится как копия.`)) return;
        try {
            const r = await ApiService.restoreCourseworkDoc(courseworkId, b.id);
            onToast({severity: 'success', message: r.message});
            loadBackups();
        } catch (e) {
            onToast({severity: 'error', message: errText(e, 'Не удалось восстановить')});
        }
    };

    const open = loadingPreview || preview || error;

    return (
        <Paper variant="outlined" sx={{p: 2, mb: 2, borderRadius: 2}}>
            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>Перенос тем в служебную записку</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                Утверждённые темы вписываются в пустые ячейки столбца «Название тем» напротив ФИО ваших студентов. Чужие строки и заполненные
                ячейки не трогаются, перед записью сохраняется копия файла. Запись — только по кнопке.
            </Typography>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} sx={{mb: 1.5}}>
                <TextField size="small" fullWidth label="Ссылка на документ (Word-файл на Google Диске)" value={url}
                           onChange={(e) => setUrl(e.target.value)}/>
                <Button variant="outlined" onClick={save} disabled={saving || url.trim() === (docUrl || '')}>Сохранить</Button>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" startIcon={<DescriptionOutlinedIcon/>} onClick={openPreview} disabled={!docUrl}>
                    Перенести утверждённые темы…
                </Button>
                {docUrl && <MuiLink href={docUrl} target="_blank" rel="noreferrer" variant="body2">Открыть документ</MuiLink>}
            </Stack>

            {backups.length > 0 && (
                <Box sx={{mt: 2}}>
                    <Typography variant="caption" color="text.secondary">Копии документа (сохраняются перед каждой записью)</Typography>
                    <Stack spacing={0.5} sx={{mt: 0.5}}>
                        {backups.slice(0, 5).map((b) => (
                            <Stack key={b.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="body2">#{b.id} · {new Date(b.createdAt).toLocaleString('ru-RU')} · {b.reason}</Typography>
                                <Button size="small" onClick={() => download(b)}>Скачать</Button>
                                <Button size="small" color="warning" onClick={() => restore(b)}>Восстановить</Button>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}

            <Dialog open={!!open} onClose={() => !writing && (setPreview(null), setError(null))} maxWidth="md" fullWidth>
                <DialogTitle>Темы → служебная записка</DialogTitle>
                <DialogContent dividers>
                    {loadingPreview && <Box display="flex" justifyContent="center" py={4}><CircularProgress/></Box>}
                    {error && <Alert severity="error">{error}</Alert>}
                    {preview && (
                        <>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Файл: <b>{preview.fileName}</b>. Будет вписано: <b>{preview.toWrite}</b>, уже стоит: {preview.alreadyDone}, расхождений: {preview.conflicts}.
                            </Typography>
                            {preview.conflicts > 0 && (
                                <Alert severity="warning" sx={{mb: 1}}>
                                    Где в документе уже другая тема, ничего не меняется — поправьте вручную.
                                </Alert>
                            )}
                            {preview.notInDocument.length > 0 && (
                                <Alert severity="info" sx={{mb: 1}}>
                                    Утверждены, но не найдены в документе по ФИО ({preview.notInDocument.length}): {preview.notInDocument.join(', ')}
                                </Alert>
                            )}
                            <Stack spacing={0.5} sx={{maxHeight: 360, overflow: 'auto'}}>
                                {preview.items.map((it, i) => (
                                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                                        <Chip size="small" color={ACTION[it.action].color} label={ACTION[it.action].label} sx={{flexShrink: 0}}/>
                                        <Typography variant="body2" sx={{minWidth: 0}}>
                                            <b>{it.fio}</b> ({it.group}) — {it.topic}
                                            {it.action === 'CONFLICT' && <i> · в документе: {it.current}</i>}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1.5}}>
                                Если в этот момент кто-то правит документ, запись не пройдёт — повторите через минуту.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPreview(null); setError(null); }} disabled={writing}>Закрыть</Button>
                    {preview && <Button variant="contained" onClick={write} disabled={writing || preview.toWrite === 0}>
                        {writing ? 'Записываю…' : `Записать в документ (${preview.toWrite})`}
                    </Button>}
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default CourseworkDocPanel;
